/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

/* eslint-disable max-len */

import { X } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { useSWRConfig } from 'swr';

import { useUserContext } from '../Root';

import { RunResultReport, SignedUrl } from '@/commons/protocols';
import { ImageCarousel } from '@/components/ImageCarousel';
import { useMutate } from '@/net/commons';

export const BugReport = ({ report }: { report?: RunResultReport }) => {
    const { projectSlug, suiteSlug, executionId, runId } = useParams();
    const { canOperate } = useUserContext().user;
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

    const formattedFeedback = report?.feedback
        .replace(
            /```(.*?)```/gs,
            '<pre class="whitespace-normal bg-slate-50 text-gray-700 mt-2 rounded-md p-2 border border-slate-200">$1</pre>',
        )
        .replace(
            /`(.*?)`/gs,
            '<pre class="whitespace-normal bg-slate-50 text-red-700 rounded-md px-2 py-1 border border-slate-200 inline-block">$1</pre><br/>',
        )
        .replace(/\\n/g, '<br/>');

    const { mutate, cache } = useSWRConfig();
    const { trigger: deleteScreenshot } = useMutate(
        `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}/report/${report?.id}/screenshot`,
        {
            method: 'DELETE',
            onSuccess: (data: any) => {
                const keyToUpdate = `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}`;
                const cachedData = cache.get(keyToUpdate)?.data;
                if (cachedData) {
                    const updatedData = {
                        ...cachedData,
                        result: {
                            ...cachedData.result,
                            report: {
                                ...cachedData.result.report,
                                screenshots: cachedData.result.report.screenshots.filter((screenshot: SignedUrl) => {
                                    return screenshot.fileName !== data.fileName;
                                }),
                            },
                        },
                    };
                    mutate(keyToUpdate, updatedData);
                }
            },
        },
    );

    return (
        <div className="bg-white rounded-lg h-full overflow-auto p-4">
            {selectedImageIndex !== null && report?.screenshots && (
                <ImageCarousel
                    images={report.screenshots}
                    currentIndex={selectedImageIndex}
                    onClose={() => setSelectedImageIndex(null)}
                    onNavigate={setSelectedImageIndex}
                />
            )}
            <div className="space-y-6">
                {/* Feedback Section */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <svg className="w-5 h-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                            />
                        </svg>
                        <h3 className="text-lg font-medium text-slate-800">Report</h3>
                    </div>
                    <div className="pl-7">
                        {formattedFeedback ? (
                            <div dangerouslySetInnerHTML={{ __html: formattedFeedback }} />
                        ) : (
                            <p className="text-slate-500 italic">No feedback provided.</p>
                        )}
                    </div>
                </section>

                {/* Screenshots Section */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <svg className="w-5 h-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                        <h3 className="text-lg font-medium text-slate-800">Screenshots</h3>
                    </div>
                    <div className="pl-7">
                        {report && report.screenshots?.length > 0 ? (
                            <div className="grid grid-cols-1 gap-4">
                                {report.screenshots.map((screenshot, index) => (
                                    <div key={index} className="relative group">
                                        <img
                                            src={screenshot.url}
                                            className="w-full h-auto rounded-lg border border-slate-200 transition-transform group-hover:scale-[1.02] aspect-[16/9] object-cover cursor-pointer"
                                            alt={`Screenshot ${index + 1}`}
                                            onClick={() => setSelectedImageIndex(index)}
                                        />
                                        <span className="absolute bottom-2 right-2 bg-black/50 text-white px-2 py-1 rounded-md text-sm">
                                            #{index + 1}
                                        </span>
                                        {canOperate && (
                                            <button
                                                onClick={() => deleteScreenshot({ fileName: screenshot.fileName })}
                                                className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X className="h-4 w-4 text-white" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-slate-500 italic">No screenshots for this report.</p>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};
